import { formatDate, formatTime } from '@/shared/formatters';

import type {
  ModoDaSimulacao,
  MotivoDaIndisponibilidade,
  ProvedorDeAssinatura,
  ResultadoDaAssinatura,
  UnidadeDeAvanco,
  SituacaoDaConversa,
} from '../types';

/**
 * A chave vem do core: sem `Object.hasOwn`, um `constructor` ou `toString` acharia a função do
 * protótipo, tipada como string, e a bolha e a exportação mostrariam o código dela.
 */
const textoPorChave = (mapa: Record<string, string>, chave: string | null): string | undefined =>
  chave !== null && Object.hasOwn(mapa, chave) ? mapa[chave] : undefined;

/** Os três autores do CA-8. Autor que o web não conhece aparece como Sistema, nunca em branco. */
const ROTULO_DO_AUTOR: Record<string, string> = {
  AGENTE: 'Lexi · agente',
  CLIENTE: 'Você · cliente',
  SISTEMA: 'Sistema',
};

export const rotuloDoAutor = (autor: string): string =>
  textoPorChave(ROTULO_DO_AUTOR, autor) ?? 'Sistema';

/**
 * Estados que o core grava no evento. Os de mensagem (DELIVERED, FAILED...) são os mesmos do
 * despacho de produção; estado desconhecido aparece como veio, para não esconder informação.
 */
const ROTULO_DO_ESTADO: Record<string, string> = {
  PENDENTE: 'Pendente',
  PENDING: 'Pendente',
  PROCESSANDO: 'Processando',
  ENVIADA: 'Enviada',
  SENT: 'Enviada',
  ENTREGUE: 'Entregue',
  DELIVERED: 'Entregue',
  LIDA: 'Lida',
  READ: 'Lida',
  RECEBIDA: 'Recebida',
  FALHOU: 'Falhou',
  FAILED: 'Falhou',
  FALHA: 'Falhou',
  EXPIRADA: 'Expirada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
  RECUSADO: 'Recusado',
};

export const rotuloDoEstado = (estado: string | null): string | null =>
  estado ? (textoPorChave(ROTULO_DO_ESTADO, estado) ?? estado) : null;

const UNIDADE_NO_SINGULAR: Record<UnidadeDeAvanco, string> = {
  MINUTOS: 'minuto',
  HORAS: 'hora',
  DIAS: 'dia',
};
const UNIDADE_NO_PLURAL: Record<UnidadeDeAvanco, string> = {
  MINUTOS: 'minutos',
  HORAS: 'horas',
  DIAS: 'dias',
};

export const textoDoTempoAvancado = (quantidade: number, unidade: UnidadeDeAvanco): string =>
  `Tempo do teste avançado em ${quantidade} ${
    quantidade === 1 ? UNIDADE_NO_SINGULAR[unidade] : UNIDADE_NO_PLURAL[unidade]
  }.`;

export const textoDaSessao = (situacao: 'ABERTA' | 'EXPIRADA'): string =>
  situacao === 'ABERTA'
    ? 'Sessão do WhatsApp aberta.'
    : 'Sessão do WhatsApp expirada. Para falar com o cliente, o agente precisa de um template.';

const MOTIVO_DE_ENCERRAMENTO: Record<string, string> = {
  AGENTE_TROCADO: 'o agente foi trocado',
  DADOS_ALTERADOS: 'os dados de início mudaram',
  NOVA_SIMULACAO: 'outra simulação foi iniciada',
  REINICIADA: 'a simulação foi reiniciada',
  DESFECHO_DO_FLOW: 'o agente encerrou a conversa',
};

/**
 * O finalizar_conversa do flow encerra a conversa com o motivo dela (ConversaFinalizadoMotivo), e
 * a simulação segue ATIVA: o texto fala da conversa, não da simulação.
 */
const MOTIVO_DE_FIM_DA_CONVERSA: Record<string, string> = {
  INATIVIDADE: 'Conversa encerrada pelo agente por inatividade do cliente.',
  MANUAL: 'Conversa encerrada pelo agente.',
  ORIGEM_ENCERRADA: 'Conversa encerrada pelo agente: a origem da conversa foi encerrada.',
};

export const textoDoEncerramento = (motivo: string | null): string => {
  const fimDaConversa = textoPorChave(MOTIVO_DE_FIM_DA_CONVERSA, motivo);
  if (fimDaConversa) return fimDaConversa;
  const porque = textoPorChave(MOTIVO_DE_ENCERRAMENTO, motivo);
  return porque ? `Simulação encerrada: ${porque}.` : 'Simulação encerrada.';
};

/**
 * O negotiation_action que encerra o caso grava o status do caso sandbox; a simulação segue
 * ATIVA, por isso o texto fala da negociação. Só acordo e sem acordo têm texto próprio: CLOSED
 * não diz como a negociação terminou e, como o status desconhecido ou ausente, cai no texto neutro.
 */
const TEXTO_DO_CASO_ENCERRADO: Record<string, string> = {
  AGREEMENT_CLOSED: 'Negociação encerrada pelo agente com acordo.',
  NO_AGREEMENT: 'Negociação encerrada pelo agente sem acordo.',
};
const TEXTO_DO_CASO_ENCERRADO_NEUTRO = 'Negociação encerrada pelo agente.';

/** A nota é o closeCaseNotes do nó, já interpolado no core: ajuda quem testa o agente. */
export const textoDoEncerramentoDoCaso = (status: string | null, nota: string | null): string => {
  const encerramento =
    textoPorChave(TEXTO_DO_CASO_ENCERRADO, status) ?? TEXTO_DO_CASO_ENCERRADO_NEUTRO;
  const notaDoAgente = nota?.trim();
  return notaDoAgente ? `${encerramento} Motivo: ${notaDoAgente}` : encerramento;
};

const MOTIVO_DE_FALHA: Record<string, string> = {
  SEM_FLOW: 'esta conversa não tem agente vinculado',
  RODADA_EM_CURSO: 'a Lexi ainda estava processando a mensagem anterior',
  PROCESSAMENTO_FALHOU: 'o processamento da mensagem falhou',
  COMANDO_INTERROMPIDO: 'o processamento foi interrompido',
  JANELA_FECHADA: 'a sessão do WhatsApp estava fechada e a mensagem não foi entregue',
  ANEXO_INVALIDO: 'o anexo foi recusado',
  DOCUMENTO_DE_TESTE_FALHOU: 'o documento de teste da assinatura não pôde ser gerado',
  FLOW_DA_ASSINATURA_NAO_ENCONTRADO:
    'o flow configurado para o retorno da assinatura não existe neste ambiente',
};

export const textoDaFalha = (codigo: string | null): string => {
  const porque = textoPorChave(MOTIVO_DE_FALHA, codigo);
  return porque ? `Falha: ${porque}.` : 'Falha ao processar este passo da conversa.';
};

export const TEXTO_DO_DIRECIONAMENTO =
  'Aguardando direcionamento. O agente pausou a conversa até alguém decidir o próximo passo.';
const TEXTO_DO_ATENDIMENTO_HUMANO =
  'Em atendimento humano. O agente parou de responder nesta conversa.';

/** O motivo é o configurado no nó do flow, não fala do cliente: ajuda quem testa o agente. */
export const textoDoAtendimentoHumano = (motivo: string | null): string =>
  motivo?.trim()
    ? `${TEXTO_DO_ATENDIMENTO_HUMANO} Motivo: ${motivo.trim()}`
    : TEXTO_DO_ATENDIMENTO_HUMANO;

/** send_email na simulação só registra: nenhum e-mail sai (CA-24). */
export const TEXTO_DO_EMAIL = 'E-mail registrado na simulação (não enviado)';

export const textoDoEventoDesconhecido = (tipo: string): string =>
  `Evento "${tipo}" sem apresentação nesta tela.`;

export const ROTULO_DA_SITUACAO: Record<SituacaoDaConversa, string> = {
  ATIVA: 'Em teste',
  ENCERRADA: 'Encerrada',
  EM_ATENDIMENTO_HUMANO: 'Em atendimento humano',
  AGUARDANDO_DIRECIONAMENTO: 'Aguardando direcionamento',
};

/** Estados do compositor do CA-9. */
export const ROTULO_DO_ENVIO = {
  pendente: 'Enviando…',
  processando: 'A Lexi está processando a mensagem.',
  entregue: 'Mensagem entregue.',
  encerrado: 'Simulação encerrada. Reinicie para conversar de novo.',
} as const;

/** `dd/MM/yyyy HH:mm` no fuso de Brasília: com o relógio virtual, o dia muda entre mensagens. */
export const dataHoraDoEvento = (instante: string): string =>
  `${formatDate(instante)} ${formatTime(instante)}`;

/**
 * O relógio virtual no formato do mock do #1333 (`toLocaleString('pt-BR')`, com segundos), mas
 * sempre no fuso de Brasília: no fuso do navegador, o relógio e os balões (`dataHoraDoEvento`)
 * mostrariam horas diferentes para o mesmo instante a quem não está em São Paulo.
 */
export const relogioDoTeste = (instante: string): string =>
  new Date(instante).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });

export const ROTULO_DO_PROVEDOR: Record<ProvedorDeAssinatura, string> = {
  ZAPSIGN: 'ZapSign',
  CLICKSIGN: 'ClickSign',
};

/** Botões da TELA-002 (CA-19). */
export const ROTULO_DA_ACAO_DE_ASSINATURA: Record<ResultadoDaAssinatura, string> = {
  CONCLUIDA: 'Concluir',
  CANCELADA: 'Cancelar',
  EXPIRADA: 'Simular expiração',
  FALHA: 'Simular falha',
};

const SEM_VOLTA = 'O resultado é registrado uma única vez e não pode ser trocado depois.';

/** Os quatro resultados são definitivos (CAS no core): todos passam por confirmação. */
export const CONFIRMACAO_DA_ACAO_DE_ASSINATURA: Record<
  ResultadoDaAssinatura,
  { titulo: string; descricao: string; confirmar: string }
> = {
  CONCLUIDA: {
    titulo: 'Concluir a assinatura de teste?',
    descricao: `O agente recebe a assinatura como concluída e segue o flow. ${SEM_VOLTA}`,
    confirmar: 'Concluir assinatura',
  },
  CANCELADA: {
    titulo: 'Cancelar a assinatura de teste?',
    descricao: `O agente recebe o cancelamento, como receberia do provedor. ${SEM_VOLTA}`,
    confirmar: 'Cancelar assinatura',
  },
  EXPIRADA: {
    titulo: 'Simular a expiração da assinatura?',
    descricao: `O agente recebe a assinatura como expirada. ${SEM_VOLTA}`,
    confirmar: 'Simular expiração',
  },
  FALHA: {
    titulo: 'Simular uma falha na assinatura?',
    descricao: `O agente recebe a falha do provedor. ${SEM_VOLTA}`,
    confirmar: 'Simular falha',
  },
};

export const RESULTADO_REGISTRADO_DA_ASSINATURA: Record<ResultadoDaAssinatura, string> = {
  CONCLUIDA: 'Assinatura de teste concluída',
  CANCELADA: 'Assinatura de teste cancelada',
  EXPIRADA: 'Expiração da assinatura simulada',
  FALHA: 'Falha da assinatura simulada',
};

/** Os dois modos da tarefa, com os nomes que ela usa. */
export const ROTULO_DO_MODO: Record<ModoDaSimulacao, string> = {
  RASCUNHO: 'Rascunho salvo',
  PUBLICADA: 'Versão publicada',
};

const MOTIVO_DA_INDISPONIBILIDADE: Record<MotivoDaIndisponibilidade, string> = {
  SEM_PUBLICACAO: 'O agente ainda não tem versão publicada.',
  INDISPONIVEL_NO_ESCRITORIO: 'O agente não está disponível para o escritório escolhido.',
  AGENTE_ARQUIVADO: 'O agente está arquivado.',
  SEM_FLUXO_PRINCIPAL: 'O agente não tem fluxo principal definido.',
  FLUXO_INATIVO: 'O fluxo não está ativo.',
};

/** Motivo novo do core ainda aparece como indisponível, em vez de deixar a opção sem explicação. */
export const textoDaIndisponibilidadeDoModo = (motivo: string | null): string =>
  textoPorChave(MOTIVO_DA_INDISPONIBILIDADE, motivo) ?? 'Indisponível para este teste.';
